// src/App.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
  collection,
  query,
  getDocs,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "./firebase";
import { ChevronLeft } from "lucide-react";

import AppHeader from './components/AppHeader';
import TopicFilter from './components/TopicFilter';
import ArticleCard from './components/ArticleCard';
import LoadingSpinner from './components/LoadingSpinner';
import MarkdownRenderer from './components/MarkdownRenderer';

// New Pages
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import ContactSupport from './components/ContactSupport';
import CopyrightNote from './components/CopyrightNote';
import DisclaimerPage from './components/DisclaimerPage';
import ImprintPage from './components/ImprintPage';
import AboutPage from './components/AboutPage';
import Footer from './components/Footer';
import AdAnalyticsPage from './components/AdAnalyticsPage';

const appId = "canada60";

// Move Topic definitions out of the component to a central place
const TOPICS = [
  { id: "all", label: "All" },
  { id: "general", label: "General" },
  { id: "world", label: "World" },
  { id: "business", label: "Business" },
  { id: "technology", label: "Tech" },
  { id: "sports", label: "Sports" },
];


const ArticlePage = ({ article, onBack }) => {
  const publishedDate = new Date(article.publishedAt);
  const readingTime = Math.max(
    1,
    Math.round(article.rewrittenContent.split(" ").length / 200)
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-gray-100 to-gray-200 dark:from-slate-950 dark:via-slate-950 dark:to-slate-950 flex flex-col items-center">
      <div className="w-full max-w-5xl p-4 lg:p-8 flex-grow">
        <button
          onClick={onBack}
          className="inline-flex items-center text-red-600 hover:text-red-800 font-medium mb-6 px-3 py-1.5 rounded-full transition-colors bg-red-50 hover:bg-red-100"
        >
          <ChevronLeft size={18} className="mr-1" />
          Back to latest stories
        </button>

        <div className="flex justify-center">
          <article className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl overflow-hidden border border-gray-200 dark:border-slate-700 w-full">
            {/* Hero image */}
            <div className="h-64 sm:h-80 bg-gray-200 dark:bg-slate-800 overflow-hidden relative">
              <img
                src={
                  article.imageUrl ||
                  "https://firebasestorage.googleapis.com/v0/b/canada60app.firebasestorage.app/o/ads%2FBriefly.png?alt=media&token=a2a9a627-0ff1-498c-83a3-805b68fcfd11"
                }
                alt={article.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src =
                    "https://firebasestorage.googleapis.com/v0/b/canada60app.firebasestorage.app/o/ads%2FBriefly.png?alt=media&token=a2a9a627-0ff1-498c-83a3-805b68fcfd11";
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
              <div className="absolute bottom-4 left-4 px-3 py-1 rounded-full bg-black/70 text-xs font-semibold text-white uppercase tracking-wide">
                {article.topic || "General"}
              </div>
            </div>

            {/* Body */}
            <div className="p-6 md:p-10">
              <div className="flex flex-wrap items-center text-xs text-gray-500 dark:text-slate-400 mb-4 gap-3">
                <span className="px-3 py-1 bg-gray-100 dark:bg-slate-800 rounded-full font-medium text-gray-700 dark:text-slate-200">
                  BRIEFLYCA
                </span>
                <span>
                  {publishedDate.toLocaleDateString()} • {readingTime} min read
                </span>
                <span>Source: {article.sourceName}</span>
              </div>

              <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-slate-50 mb-3 leading-snug tracking-tight">
                {article.title}
              </h1>
              <p className="text-lg text-gray-600 dark:text-slate-200 !text-gray-600 dark:!text-slate-200 italic mb-6 border-b border-gray-200 dark:border-slate-700 pb-6">
                {article.summary}
              </p>

              <div className="max-w-none text-lg">
                <MarkdownRenderer markdown={article.rewrittenContent} />
              </div>
            </div>
          </article>
        </div>
      </div>
      <Footer />
    </div>
  );
};

const HomePage = ({ onOpenArticle, theme, onToggleTheme }) => {
  const [articles, setArticles] = useState([]);
  const [topic, setTopic] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  // Pagination state
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 16;

  useEffect(() => {
    // Reset pagination when filter changes
    setPage(1);
  }, [topic, search]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const colRef = collection(
          db,
          "artifacts",
          appId,
          "public",
          "data",
          "canonical_news"
        );

        // Fetch a larger pool of latest articles for client-side filtering/pagination
        // This avoids complex Firestore index requirements for sorting + filtering
        const q = query(colRef, orderBy("publishedAt", "desc"), limit(100));
        const snap = await getDocs(q);

        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setArticles(list);

      } catch (e) {
        console.error("Error fetching articles:", e);
      }
      setLoading(false);
    };
    load();
  }, []); // Load once on mount

  // Filter for topic and search client-side
  const filtered = useMemo(() => {
    return articles.filter((a) => {
      const matchesTopic = topic === "all" || a.topic === topic;
      const queryText = `${a.title} ${a.summary}`.toLowerCase();
      const matchesSearch = queryText.includes(search.toLowerCase());
      return matchesTopic && matchesSearch;
    });
  }, [articles, topic, search]);

  // Pagination Logic
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const hasMore = page < totalPages;

  const startIndex = (page - 1) * PAGE_SIZE;
  const paginatedArticles = filtered.slice(startIndex, startIndex + PAGE_SIZE);

  const handleNext = () => {
    if (hasMore) {
      setPage(p => p + 1);
    }
  };

  const handlePrev = () => {
    setPage(p => Math.max(1, p - 1));
  };

  // Identify hero (only on first page?) 
  const showHero = page === 1 && paginatedArticles.length > 0;
  const heroArticle = showHero ? paginatedArticles[0] : null;
  // If showing hero, remove it from the grid list so it's not duplicated
  const listArticles = showHero ? paginatedArticles.slice(1) : paginatedArticles;

  if (loading) return <LoadingSpinner message="Loading latest stories..." />;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-gray-100 to-gray-200 dark:from-slate-950 dark:via-slate-950 dark:to-slate-950 flex flex-col items-center">
      <main className="w-full max-w-7xl px-4 py-5 lg:px-8 lg:py-8 space-y-6 flex-grow">
        <AppHeader
          search={search}
          onSearchChange={setSearch}
          theme={theme}
          onToggleTheme={onToggleTheme}
        />

        <TopicFilter topics={TOPICS} currentTopic={topic} onTopicChange={setTopic} />

        {/* Hero Story Section - Only on Page 1 */}
        {heroArticle && (
          <section className="mt-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-gray-500">
                Top Story
              </h2>
              <span className="text-xs text-gray-400">
                Updated {new Date(heroArticle.publishedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            </div>
            <ArticleCard
              article={heroArticle}
              onOpenArticle={onOpenArticle}
              isHero={true}
            />
          </section>
        )}

        {/* Article Grid Section */}
        <section className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-gray-500">
              Latest Updates
            </h2>
            <span className="text-xs text-gray-400">
              Page {page} • Showing {listArticles.length} stories
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {listArticles.map((a) => (
              <ArticleCard key={a.id} article={a} onOpenArticle={onOpenArticle} />
            ))}

            {listArticles.length === 0 && (
              <p className="text-center text-gray-500 col-span-full mt-10">
                {page === 1 ? "No stories found matching your filters." : "No more stories on this page."}
              </p>
            )}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-12 mb-8">
              <button
                onClick={handlePrev}
                disabled={page === 1}
                className={`px-4 py-2 rounded-full font-medium transition-colors border ${page === 1
                  ? "border-gray-200 text-gray-400 cursor-not-allowed dark:border-slate-800 dark:text-slate-600"
                  : "border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
                  }`}
              >
                Previous
              </button>
              <span className="text-sm text-gray-500 dark:text-slate-400 font-medium">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={handleNext}
                disabled={!hasMore}
                className={`px-4 py-2 rounded-full font-medium transition-colors border ${!hasMore
                  ? "border-gray-200 text-gray-400 cursor-not-allowed dark:border-slate-800 dark:text-slate-600"
                  : "border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
                  }`}
              >
                Next
              </button>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default function App() {
  const [currentArticle, setCurrentArticle] = useState(null);
  const [articleLoading, setArticleLoading] = useState(false);
  const [error, setError] = useState(null);
  const [theme, setTheme] = useState(() => {
    try {
      const stored = localStorage.getItem("theme");
      if (stored === "light" || stored === "dark") {
        return stored;
      }
      return window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
    } catch {
      return "light";
    }
  });

  // Apply to <html> and persist
  useEffect(() => {
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    try {
      localStorage.setItem("theme", theme);
    } catch {
      // ignore
    }
  }, [theme]);

  const toggleTheme = () =>
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));

  // Determine route: prioritize hash for article navigation, fallback to pathname for static pages
  const hashRoute = window.location.hash.substring(1);
  const pathnameRoute = window.location.pathname;
  const route = hashRoute || pathnameRoute || "/";
  const match = route.match(/^\/article\/([^/]+)$/);
  const articleIdFromHash = match ? match[1] : null;

  useEffect(() => {
    const onHashChange = () => {
      window.location.reload();
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    const loadArticle = async () => {
      if (!articleIdFromHash) {
        setCurrentArticle(null);
        return;
      }
      setArticleLoading(true);
      setError(null);
      try {
        const docRef = collection(
          db,
          "artifacts",
          appId,
          "public",
          "data",
          "canonical_news"
        );
        const snap = await getDocs(docRef);
        const doc = snap.docs.find((d) => d.id === articleIdFromHash);
        if (!doc) {
          setError("Article not found.");
          setCurrentArticle(null);
        } else {
          setCurrentArticle({ id: doc.id, ...doc.data() });
        }
      } catch (e) {
        console.error(e);
        setError("Failed to load article.");
      } finally {
        setArticleLoading(false);
      }
    };
    loadArticle();
  }, [articleIdFromHash]);

  if (route === '/privacy') return <PrivacyPolicy />;
  if (route === '/terms') return <TermsOfService />;
  if (route === '/contact') return <ContactSupport />;
  if (route === '/copyright') return <CopyrightNote />;
  if (route === '/disclaimer') return <DisclaimerPage />;
  if (route === '/imprint') return <ImprintPage />;
  if (route === '/about') return <AboutPage />;
  if (route === '/admin') return <AdAnalyticsPage />;

  const openArticle = (id) => {
    window.location.hash = `/article/${id}`;
  };

  if (articleIdFromHash) {
    if (articleLoading) return <LoadingSpinner message="Loading article..." />;
    if (error) return <LoadingSpinner message={error} />;
    if (!currentArticle) return <LoadingSpinner message="Article not found." />;
    return (
      <ArticlePage
        article={currentArticle}
        onBack={() => {
          window.location.hash = "/";
        }}
      />
    );
  }

  return <HomePage onOpenArticle={openArticle} theme={theme} onToggleTheme={toggleTheme} />;
}
